/**
 * Work items — V98-1-4 / ADR-0046 (migration 231).
 *
 * A work item is one owned action. It links once to every consumer it
 * satisfies (research §5.1 principle 3: "one action, many consumers, counted
 * once") — e.g. one upload of a vendor's SOC 2 report answers the 2026 PBC
 * request and the 2027 period check. Completing the item stamps every linked
 * consumer as satisfied; every query returns each item once, whatever number
 * of consumers it serves.
 *
 * Ownership is an `owner` assignment on subject (work_item, work_item_id), so
 * the item appears in the owner's hats, moves with /assignments reassignment,
 * and routes can guard it with requireAssignmentOrRole. Owner hats end when the
 * item reaches a terminal status.
 *
 * Derived items (kind 'derived', V98-1-5) carry cause_kind + dedupe_key; the
 * partial unique index allows one OPEN item per (workspace, dedupe_key).
 *
 * Authorization is the caller's job (route layer): completing / closing an
 * item should require its `owner` assignment or an admin role.
 */
import { legalDeterminationsProgram, programsWhere } from '../programs/manifests.js';
import { generateRandomId } from '../utils/audit-crypto.js';
import { assertAssignable, endAssignment, ensureAssignment } from './assignment-service.js';
import { queryInChunks, sqlIn } from '../utils/sql-in.js';
export const WORK_ITEM_KINDS = ['task', 'evidence', 'document', 'review', 'derived'];
export const WORK_ITEM_STATUSES = ['open', 'done', 'resolved', 'cancelled'];
/**
 * What a work item can satisfy. Validated here rather than by a CHECK so
 * later tracks (persisted PBC requests, migration 241) add kinds without a
 * table swap.
 */
export const WORK_ITEM_CONSUMER_KINDS = [
    'pbc_request', 'requirement', 'control', 'period_check', 'obligation_occurrence',
    'finding', 'evidence_request', 'audit_period', 'subservice_org', 'external_service',
    'connector', 'system', 'ai_law_rule', 'determination', 'program', 'policy', 'scn', 'access_review',
];
// ---- errors --------------------------------------------------------------------
export class WorkItemValidationError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'WorkItemValidationError';
    }
}
export class WorkItemNotFoundError extends Error {
    constructor(id) {
        super(`Work item not found: ${id}`);
        this.name = 'WorkItemNotFoundError';
    }
}
export class WorkItemStateError extends Error {
    constructor(message) {
        super(message);
        this.name = 'WorkItemStateError';
    }
}
/** An open item with the same dedupe key already exists. */
export class WorkItemDuplicateError extends Error {
    dedupe_key;
    constructor(dedupe_key) {
        super(`An open work item already exists for ${dedupe_key}`);
        this.dedupe_key = dedupe_key;
        this.name = 'WorkItemDuplicateError';
    }
}
// ---- helpers -------------------------------------------------------------------
const CONSUMER_SET = new Set(WORK_ITEM_CONSUMER_KINDS);
const KIND_SET = new Set(WORK_ITEM_KINDS);
const TITLE_MAX = 300;
const TEXT_MAX = 4000;
const MAX_CONSUMERS = 50;
export function isWorkItemConsumerKind(v) {
    return typeof v === 'string' && CONSUMER_SET.has(v);
}
function validateConsumer(c) {
    if (!isWorkItemConsumerKind(c?.kind)) {
        throw new WorkItemValidationError('invalid_consumer_kind', `Unknown consumer kind: ${String(c?.kind)}`);
    }
    if (typeof c.id !== 'string' || c.id.length < 1 || c.id.length > 200) {
        throw new WorkItemValidationError('invalid_consumer_id', 'consumer id must be 1–200 characters');
    }
    if (c.period_id != null && (typeof c.period_id !== 'string' || c.period_id.length > 200)) {
        throw new WorkItemValidationError('invalid_period_id', 'period_id must be at most 200 characters');
    }
}
function optIso(v, field) {
    if (v == null)
        return null;
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime()))
        throw new WorkItemValidationError('invalid_date', `${field} is not a valid date`);
    return d.toISOString();
}
function optText(v, field, max = TEXT_MAX) {
    if (v == null)
        return null;
    const t = String(v).trim();
    if (!t)
        return null;
    if (t.length > max)
        throw new WorkItemValidationError('too_long', `${field} must be at most ${max} characters`);
    return t;
}
function dedupeConsumers(consumers) {
    const seen = new Set();
    return consumers.filter((c) => {
        const k = `${c.kind}\u0000${c.id}`;
        if (seen.has(k))
            return false;
        seen.add(k);
        return true;
    });
}
const isUnique = (err) => /unique|duplicate/i.test(String(err?.message ?? err));
function linkInsert(row, createdAt) {
    return {
        sql: `INSERT INTO work_item_links
            (link_id, work_item_id, workspace_id, consumer_kind, consumer_id, period_id,
             satisfied_at, satisfied_by_user_id, created_by, created_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [
            row.link_id, row.work_item_id, row.workspace_id, row.consumer_kind, row.consumer_id, row.period_id,
            row.satisfied_at, row.satisfied_by_user_id, row.created_by, createdAt,
        ],
    };
}
async function loadItem(db, workspaceId, workItemId) {
    const r = await db.query(`SELECT * FROM work_items WHERE work_item_id = ? AND workspace_id = ? LIMIT 1`, [workItemId, workspaceId]);
    if (!r.results[0])
        throw new WorkItemNotFoundError(workItemId);
    return r.results[0];
}
async function hydrate(db, workspaceId, items, now) {
    if (items.length === 0)
        return [];
    const ids = items.map((i) => i.work_item_id);
    const nowIso = now.toISOString();
    // listWorkItems hydrates up to 500 items — past D1's 100-parameter limit — on
    // the tenant plane (may be Postgres): chunked by work item. Rows are grouped
    // per item below and an item's rows all sit in one chunk, so each chunk's
    // ORDER BY is all the per-item order needs.
    const [links, owners] = await Promise.all([
        queryInChunks(db, ids, (marks, chunk) => ({
            sql: `SELECT * FROM work_item_links WHERE workspace_id = ? AND work_item_id IN (${marks})
        ORDER BY consumer_kind ASC, consumer_id ASC`,
            params: [workspaceId, ...chunk],
        })),
        queryInChunks(db, ids, (marks, chunk) => ({
            sql: `SELECT subject_id, user_id FROM assignments
        WHERE workspace_id = ? AND subject_kind = 'work_item' AND responsibility = 'owner'
          AND subject_id IN (${marks})
          AND effective_from <= ? AND (effective_to IS NULL OR effective_to > ?)
        ORDER BY user_id ASC`,
            params: [workspaceId, ...chunk, nowIso, nowIso],
        })),
    ]);
    return items.map((i) => ({
        ...i,
        owner_user_ids: owners.filter((o) => o.subject_id === i.work_item_id).map((o) => o.user_id),
        links: links.filter((l) => l.work_item_id === i.work_item_id),
    }));
}
async function endOwnerHats(db, workspaceId, workItemId, actor, reason, now) {
    const open = await db.query(`SELECT assignment_id FROM assignments
      WHERE workspace_id = ? AND subject_kind = 'work_item' AND subject_id = ? AND effective_to IS NULL`, [workspaceId, workItemId]);
    for (const a of open.results) {
        await endAssignment(db, { workspace_id: workspaceId, assignment_id: a.assignment_id, actor_user_id: actor, reason, now });
    }
}
export async function createWorkItem(db, input) {
    if (!KIND_SET.has(input.kind))
        throw new WorkItemValidationError('invalid_kind', `Unknown work item kind: ${String(input.kind)}`);
    const title = optText(input.title, 'title', TITLE_MAX);
    if (!title)
        throw new WorkItemValidationError('title_required', 'A title is required');
    const detail = optText(input.detail, 'detail');
    const dedupe = optText(input.dedupe_key, 'dedupe_key', 400);
    const cause = optText(input.cause_kind, 'cause_kind', 100);
    if (input.kind === 'derived' && (!dedupe || !cause)) {
        throw new WorkItemValidationError('derived_needs_cause', 'Derived work items need cause_kind and dedupe_key');
    }
    const consumers = dedupeConsumers(input.consumers ?? []);
    if (consumers.length > MAX_CONSUMERS) {
        throw new WorkItemValidationError('too_many_consumers', `At most ${MAX_CONSUMERS} consumers per work item`);
    }
    consumers.forEach(validateConsumer);
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const newId = input.newId ?? (() => generateRandomId('wit'));
    const id = input.work_item_id ?? newId();
    await assertAssignable(db, {
        workspace_id: input.workspace_id, user_id: input.owner_user_id, actor_user_id: input.actor_user_id,
        subjects: [{ subject: { kind: 'work_item', id }, responsibility: 'owner' }], now,
    });
    const statements = [
        {
            sql: `INSERT INTO work_items
              (work_item_id, workspace_id, kind, title, detail, status, due_at, hard_date, source_kind, source_id,
               cause_kind, dedupe_key, created_by, created_at, updated_at)
            VALUES (?, ?, ?, ?, ?, 'open', ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            params: [
                id, input.workspace_id, input.kind, title, detail, optIso(input.due_at, 'due_at'), optIso(input.hard_date, 'hard_date'),
                optText(input.source_kind, 'source_kind', 100), optText(input.source_id, 'source_id', 200),
                cause, dedupe, input.actor_user_id, nowIso, nowIso,
            ],
        },
        ...consumers.map((c) => linkInsert({
            link_id: generateRandomId('wil'), work_item_id: id, workspace_id: input.workspace_id,
            consumer_kind: c.kind, consumer_id: c.id, period_id: c.period_id ?? null,
            satisfied_at: null, satisfied_by_user_id: null, created_by: input.actor_user_id,
        }, nowIso)),
    ];
    try {
        await db.batch(statements);
    }
    catch (err) {
        if (dedupe && isUnique(err))
            throw new WorkItemDuplicateError(dedupe);
        throw err;
    }
    await ensureAssignment(db, {
        workspace_id: input.workspace_id, subject: { kind: 'work_item', id }, responsibility: 'owner',
        user_id: input.owner_user_id, actor_user_id: input.actor_user_id, now,
    });
    return (await getWorkItem(db, input.workspace_id, id, now));
}
/**
 * Link more consumers to an item (idempotent per consumer). Linking to an
 * item that is already done records the consumer as satisfied by it.
 */
export async function linkWorkItem(db, input) {
    const consumers = dedupeConsumers(input.consumers ?? []);
    if (consumers.length === 0)
        throw new WorkItemValidationError('no_consumers', 'At least one consumer is required');
    if (consumers.length > MAX_CONSUMERS) {
        throw new WorkItemValidationError('too_many_consumers', `At most ${MAX_CONSUMERS} consumers per call`);
    }
    consumers.forEach(validateConsumer);
    const item = await loadItem(db, input.workspace_id, input.work_item_id);
    if (item.status === 'resolved' || item.status === 'cancelled') {
        throw new WorkItemStateError(`Work item ${item.work_item_id} is ${item.status}`);
    }
    const now = input.now ?? new Date();
    const existing = await db.query(`SELECT consumer_kind, consumer_id FROM work_item_links WHERE work_item_id = ? AND workspace_id = ?`, [item.work_item_id, input.workspace_id]);
    const have = new Set(existing.results.map((l) => `${l.consumer_kind}\u0000${l.consumer_id}`));
    const fresh = consumers.filter((c) => !have.has(`${c.kind}\u0000${c.id}`));
    if (fresh.length > 0) {
        const done = item.status === 'done';
        try {
            await db.batch(fresh.map((c) => linkInsert({
                link_id: generateRandomId('wil'), work_item_id: item.work_item_id, workspace_id: input.workspace_id,
                consumer_kind: c.kind, consumer_id: c.id, period_id: c.period_id ?? null,
                satisfied_at: done ? item.completed_at : null, satisfied_by_user_id: done ? item.completed_by_user_id : null,
                created_by: input.actor_user_id,
            }, now.toISOString())));
        }
        catch (err) {
            if (!isUnique(err))
                throw err; // a concurrent link of the same consumer — already there
        }
    }
    return (await getWorkItem(db, input.workspace_id, item.work_item_id, now));
}
/** open → done; every linked consumer is stamped satisfied in the same batch. */
export async function completeWorkItem(db, input) {
    const item = await loadItem(db, input.workspace_id, input.work_item_id);
    if (item.status !== 'open')
        throw new WorkItemStateError(`Work item ${item.work_item_id} is ${item.status}`);
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const res = await db.batch([
        {
            sql: `UPDATE work_items SET status = 'done', completed_at = ?, completed_by_user_id = ?, completion_ref = ?,
                   closed_at = ?, closed_reason = 'completed', updated_at = ?
             WHERE work_item_id = ? AND workspace_id = ? AND status = 'open'`,
            params: [nowIso, input.actor_user_id, optText(input.completion_ref, 'completion_ref', 400), nowIso, nowIso,
                item.work_item_id, input.workspace_id],
        },
        {
            sql: `UPDATE work_item_links SET satisfied_at = ?, satisfied_by_user_id = ?
             WHERE work_item_id = ? AND workspace_id = ? AND satisfied_at IS NULL`,
            params: [nowIso, input.actor_user_id, item.work_item_id, input.workspace_id],
        },
    ]);
    if ((res?.[0]?.meta?.changes ?? 1) === 0) {
        throw new WorkItemStateError(`Work item ${item.work_item_id} changed concurrently`);
    }
    await endOwnerHats(db, input.workspace_id, item.work_item_id, input.actor_user_id, 'Work item completed', now);
    return (await getWorkItem(db, input.workspace_id, item.work_item_id, now));
}
/** open → resolved | cancelled (no consumer is marked satisfied). */
export async function closeWorkItem(db, input) {
    if (input.status !== 'resolved' && input.status !== 'cancelled') {
        throw new WorkItemValidationError('invalid_status', 'status must be resolved or cancelled');
    }
    const reason = optText(input.reason, 'reason', 400);
    if (!reason)
        throw new WorkItemValidationError('reason_required', 'A reason is required');
    const item = await loadItem(db, input.workspace_id, input.work_item_id);
    if (item.status !== 'open')
        throw new WorkItemStateError(`Work item ${item.work_item_id} is ${item.status}`);
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    await db.execute(`UPDATE work_items SET status = ?, closed_at = ?, closed_reason = ?, updated_at = ?
      WHERE work_item_id = ? AND workspace_id = ? AND status = 'open'`, [input.status, nowIso, reason, nowIso, item.work_item_id, input.workspace_id]);
    await endOwnerHats(db, input.workspace_id, item.work_item_id, input.actor_user_id, `Work item ${input.status}`, now);
    return (await getWorkItem(db, input.workspace_id, item.work_item_id, now));
}
// ---- subtype + the action to take (V98-3 review S10) -------------------------------------
/**
 * What kind of work a work item is, so every surface can offer the action
 * that advances it (no dead ends): a legal determination, an SCN
 * classification, a coverage request (a provider's SOC report / bridge
 * letter), evidence, a revise-and-resubmit, a disputed-approval resolution,
 * or a plain task.
 */
export const WORK_ITEM_SUBTYPES = [
    'determination', 'scn_classification', 'coverage_request', 'evidence', 'revise', 'resolve_dispute', 'task',
];
const enc = encodeURIComponent;
/** Where legal determinations are decided (the AI Regulatory program page). */
const LEGAL_PROGRAM = enc(legalDeterminationsProgram() ?? '');
/** A coverage item counted in no program links to the first program (manifest order) that takes coverage documents. */
const COVERAGE_FALLBACK_PROGRAM = programsWhere((m) => m.coverage.documentKinds.length > 0)[0]?.key ?? '';
/**
 * S10: subtype + action from what the work item knows (source, derived
 * cause, linked consumers). `programKeys` are the program keys it counts in
 * (soc2, fedramp-cr26, ai-regulatory, …) for links into a program page.
 * Pure.
 */
export function describeWorkItem(w, links, programKeys = []) {
    const link = (k) => links.find((l) => l.consumer_kind === k);
    const complete = {
        kind: 'endpoint', label: 'Mark done', record: { kind: 'work_item', id: w.work_item_id },
        method: 'POST', path: `/work-items/${enc(w.work_item_id)}/complete`,
    };
    if (w.source_kind === 'signature_request' && w.source_id) {
        return { subtype: 'revise', act: { kind: 'endpoint', label: 'Revise and resubmit', record: { kind: 'signature_request', id: w.source_id },
                method: 'POST', path: `/signatures/${enc(w.source_id)}/resubmit` } };
    }
    if (w.source_kind === 'independent_party_request' && w.source_id) {
        // V98-3 review R3-S5: an ask to add an independent party for an access review (closes when a seat is added for it).
        return { subtype: 'task', act: { kind: 'endpoint', label: 'Add an independent party', record: { kind: 'access_review', id: w.source_id },
                method: 'POST', path: '/coverage/independent-parties', href: `/items/access_review/${enc(w.source_id)}` } };
    }
    if (w.source_kind === 'solo_self_grant') {
        // V98-7-3 H1: a solo owner holds a sensitive hat they granted themselves — seat an independent party to review it.
        return { subtype: 'task', act: { kind: 'endpoint', label: 'Add an independent party', record: w.source_id ? { kind: 'assignment', id: w.source_id } : null,
                method: 'POST', path: '/coverage/independent-parties', href: '/today' } };
    }
    if (w.source_kind === 'compensating_review' && w.source_id) {
        return { subtype: 'resolve_dispute', act: { kind: 'endpoint', label: 'Re-route or revoke', record: { kind: 'compensating_review', id: w.source_id },
                method: 'POST', path: `/coverage/compensating-reviews/${enc(w.source_id)}/resolve` } };
    }
    if ((w.source_kind === 'ai_legal_ask' || w.source_kind === 'ai_owner_redirect') && w.source_id) {
        // V98-4 review T4-B2 / "Not me": a member's question about one cell, or the owner's redirect → the cell.
        return { subtype: 'determination', act: { kind: 'link', label: w.source_kind === 'ai_legal_ask' ? 'Answer' : 'Ask again',
                record: { kind: 'determination', id: w.source_id }, href: `/programs/${LEGAL_PROGRAM}/scope?cell=${enc(w.source_id)}` } };
    }
    if (w.source_kind === 'ai_policy_ask') {
        // V98-4 review T4-H8: an ask to allow AI legislation — the scope shows the locked line with "Change org policy".
        return { subtype: 'task', act: { kind: 'link', label: 'Review org policy', record: null, href: `/programs/${LEGAL_PROGRAM}/scope#ai-ceiling` } };
    }
    const det = link('determination');
    const law = link('ai_law_rule');
    if (w.cause_kind === 'in_force_ai_law_gap' || det || law) {
        // V98-4-2: the cell editor on the AI Regulatory program (decide, or record an interim position). V98-4 review:
        // one action label everywhere the item appears ("Decide", as on the decide row).
        return { subtype: 'determination', act: { kind: 'link', label: 'Decide',
                record: det ? { kind: 'determination', id: det.consumer_id } : law ? { kind: 'ai_law_rule', id: law.consumer_id } : null,
                href: det ? `/programs/${LEGAL_PROGRAM}/scope?cell=${enc(det.consumer_id)}` : `/programs/${LEGAL_PROGRAM}/scope` } };
    }
    if (w.source_kind === 'management_response') {
        // V98-5-7: write and submit the management response to a known deviation (its own page).
        return { subtype: 'task', act: { kind: 'link', label: 'Write the response', record: { kind: 'work_item', id: w.work_item_id }, href: `/responses/${enc(w.work_item_id)}` } };
    }
    if (w.source_kind === 'scn_engage_assessor' && w.source_id) {
        // V98-5-3: a transformative change — engage the assessor, then mark it done (closes the finding).
        return { subtype: 'task', act: { ...complete, label: 'Mark the assessor engaged', href: `/items/scn/${enc(w.source_id)}` } };
    }
    const scn = link('scn');
    if (scn) {
        // V98-5-3: classification is a decision (suggested vs decided, D-98-13), taken on the change's item page.
        return { subtype: 'scn_classification', act: { kind: 'endpoint', label: 'Classify the change', record: { kind: 'scn', id: scn.consumer_id },
                method: 'POST', path: `/significant-changes/${enc(scn.consumer_id)}/classify`, href: `/items/scn/${enc(scn.consumer_id)}` } };
    }
    const ext = link('external_service');
    const sub = link('subservice_org');
    if (ext || sub || w.cause_kind === 'subservice_coverage_gap' || w.cause_kind === 'unlisted_federal_data_provider') {
        const program = programKeys[0] ?? COVERAGE_FALLBACK_PROGRAM;
        if (ext) {
            return { subtype: 'coverage_request', act: { kind: 'endpoint', label: 'Add the coverage document', record: { kind: 'external_service', id: ext.consumer_id },
                    method: 'POST', path: `/programs/${enc(program)}/external-services/${enc(ext.consumer_id)}/documents`, href: `/programs/${enc(program)}/scope` } };
        }
        return { subtype: 'coverage_request', act: { kind: 'link', label: 'Open external services', record: sub ? { kind: 'subservice_org', id: sub.consumer_id } : null,
                href: `/programs/${enc(program)}/scope` } };
    }
    if (w.kind === 'evidence' || ['evidence_request', 'pbc_request', 'requirement', 'control', 'period_check'].some((k) => link(k))) {
        return { subtype: 'evidence', act: { ...complete, label: 'Add evidence and mark done' } };
    }
    return { subtype: 'task', act: complete };
}
// ---- reads ---------------------------------------------------------------------
export async function getWorkItem(db, workspaceId, workItemId, now = new Date()) {
    const r = await db.query(`SELECT * FROM work_items WHERE work_item_id = ? AND workspace_id = ? LIMIT 1`, [workItemId, workspaceId]);
    if (!r.results[0])
        return null;
    return (await hydrate(db, workspaceId, r.results, now))[0];
}
function filterSql(workspaceId, f) {
    const clauses = ['w.workspace_id = ?'];
    const params = [workspaceId];
    const statuses = f.status === undefined ? [] : Array.isArray(f.status) ? f.status : [f.status];
    if (statuses.length) {
        // d1-in-list: bounded — statuses is a subset of the WORK_ITEM_STATUSES enum
        clauses.push(`w.status IN (${statuses.map(() => '?').join(', ')})`);
        params.push(...statuses);
    }
    if (f.cause_kind) {
        clauses.push('w.cause_kind = ?');
        params.push(f.cause_kind);
    }
    if (f.owner_user_id) {
        const nowIso = (f.now ?? new Date()).toISOString();
        clauses.push(`EXISTS (SELECT 1 FROM assignments a
                    WHERE a.workspace_id = w.workspace_id AND a.subject_kind = 'work_item' AND a.subject_id = w.work_item_id
                      AND a.responsibility = 'owner' AND a.user_id = ?
                      AND a.effective_from <= ? AND (a.effective_to IS NULL OR a.effective_to > ?))`);
        params.push(f.owner_user_id, nowIso, nowIso);
    }
    if (f.consumers && f.consumers.length) {
        // One JSON parameter of `kind:id` keys (was two parameters per consumer;
        // D1 binds at most 100). Consumer kinds are enum words with no ':'. Keeps
        // the list query's ORDER BY / LIMIT whole; Postgres rewrites json_each(?).
        const keys = sqlIn(f.consumers.map((c) => `${c.kind}:${c.id}`));
        clauses.push(`EXISTS (SELECT 1 FROM work_item_links l WHERE l.work_item_id = w.work_item_id AND l.workspace_id = w.workspace_id
                    AND (l.consumer_kind || ':' || l.consumer_id) IN ${keys.sql})`);
        params.push(...keys.params);
    }
    return { where: clauses.join(' AND '), params };
}
/** Each matching item once (EXISTS filters — never a join that fans out per consumer). */
export async function listWorkItems(db, workspaceId, filter = {}) {
    const { where, params } = filterSql(workspaceId, filter);
    const limit = Math.min(Math.max(filter.limit ?? 200, 1), 500);
    const r = await db.query(`SELECT w.* FROM work_items w WHERE ${where}
      ORDER BY CASE WHEN w.hard_date IS NULL THEN 1 ELSE 0 END, w.hard_date ASC,
               CASE WHEN w.due_at IS NULL THEN 1 ELSE 0 END, w.due_at ASC, w.created_at ASC, w.work_item_id ASC
      LIMIT ${limit}`, params);
    return hydrate(db, workspaceId, r.results, filter.now ?? new Date());
}
/** The count that matches listWorkItems: one per item, however many consumers it serves. */
export async function countWorkItems(db, workspaceId, filter = {}) {
    const { where, params } = filterSql(workspaceId, filter);
    const r = await db.query(`SELECT COUNT(*) AS n FROM work_items w WHERE ${where}`, params);
    return Number(r.results[0]?.n ?? 0);
}
/** Is this consumer satisfied, and by which item? */
export async function getConsumerStatus(db, workspaceId, consumer) {
    validateConsumer(consumer);
    const r = await db.query(`SELECT work_item_id, satisfied_at FROM work_item_links
      WHERE workspace_id = ? AND consumer_kind = ? AND consumer_id = ?
      ORDER BY CASE WHEN satisfied_at IS NULL THEN 1 ELSE 0 END, satisfied_at ASC, work_item_id ASC`, [workspaceId, consumer.kind, consumer.id]);
    const first = r.results.find((l) => l.satisfied_at !== null) ?? null;
    return {
        satisfied: first !== null,
        satisfied_at: first?.satisfied_at ?? null,
        work_item_ids: [...new Set(r.results.map((l) => l.work_item_id))],
        satisfied_by_work_item_id: first?.work_item_id ?? null,
    };
}
//# sourceMappingURL=work-item-service.js.map